import { Button } from "@component/button";
import { CVDataInfo } from "@type/cv";
import { FileText } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { getCurrentCVURL, getMyCV, uploadCV, uploadTempCV } from "@service/cv";
import { maxFileSize } from "@constant/app";
import { trackEvent } from "@util/mixpanel";
import { catchErrorLog } from "@util/sentry";
import { parseFormat } from "@util/date";
import UploadCVWarningDialog from "./dialog/dialog-upload-cv-warning";

interface Props {
    isLoadingApply: boolean;
    onContinue: (isUploadNewCV: boolean) => void;
}

export default function ExistingCVProfileContainer(props: Props) {
    const [hasUploadedCV, setHasUploadedCV] = useState(false);
    const [existingCVProfile, setExistingCVProfile] = useState<CVDataInfo | undefined>(undefined)
    const [isOpenUploadCVWarning, setIsOpenUploadCVWarning] = useState<boolean>(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isUploadNewCV, setIsUploadNewCV] = useState(false);
    const [cvURL, setCVURL] = useState<string>("");
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        getMyCV().then((resp) => {
            setExistingCVProfile(resp.data);
        })

        getCurrentCVURL().then((resp) => {
            console.log("Current CV URL:", resp.data);
            setCVURL(resp.data.data.cv_url);
        })
    }, [])

    const validateFile = (file: File): string | null => {
        if (file.size > maxFileSize) {
            return "File size must be less than 5MB";
        }
        return null;
    };

    const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files === null || e.target.files.length === 0) {
            console.log("No file selected");
            return;
        }

        const file = e.target.files[0];
        const error = validateFile(file);
        if (error) {
            console.log(error);
            return;
        }

        setIsUploading(true);
        uploadTempCV(file).then((resp) => {
            if (resp.success) {
                const cvUuid = resp.data.filename;
                sessionStorage.setItem("__TEMP_CVID__", cvUuid);
                trackEvent('reupload_cv', {
                    resume_uploaded: true
                })
                return getMyCV().then((resp) => {
                    setExistingCVProfile(resp.data);
                    setIsUploadNewCV(true);
                })
            }
        }).catch((error) => {
            catchErrorLog(`Error during upload/registration: ${error}`);
        }).finally(() => {
            setIsUploading(false);
            setHasUploadedCV(true);
        })

        // uploadCV(file).then((resp) => {
        //     console.log("Upload response:", resp);
        //     if (resp.success) {
        //         trackEvent('reupload_cv', {
        //             resume_uploaded: true
        //         })
        //         return getMyCV().then((resp) => {
        //             setExistingCVProfile(resp.data);
        //             sessionStorage.setItem("__TEMP_CVID__", resp.data?.filename as string);
        //             setIsUploadNewCV(true);
        //         })
        //     }
        // }).catch((error) => {
        //     catchErrorLog(`Error during upload/registration: ${error}`);
        // }).finally(() => {
        //     setIsUploading(false);
        //     setHasUploadedCV(true);
        // })
    };

    const handleUploadConfirm = () => {
        setIsOpenUploadCVWarning(false);
        // Trigger the file input click
        fileInputRef.current?.click();
    };

    const handleUploadCancel = () => {
        setIsOpenUploadCVWarning(false);
    };

    return (
        <>
            <div className="flex overflow-hidden bg-white dark:bg-gray-950 w-full pb-10">
                <div
                    className="flex items-center justify-center bg-gray-50 dark:bg-gray-900 transition-all duration-300 w-full"
                    style={{ backgroundColor: "#ffffff" }}
                >
                    <div
                        className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 max-w-md w-full mx-4"
                        style={{ border: "1px solid #bfdbfe" }}
                    >
                        {/* Header */}
                        <div className="text-center mb-6">
                            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                                We've found an existing profile
                            </h2>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                We've saved your details from a past application. <br />
                                You may continue with your existing CV, or upload a new one to
                                update your profile.
                            </p>
                        </div>

                        {/* CV Section */}
                        <div className="space-y-4 mb-6">
                            {/* Combined CV Display and Upload */}
                            {!isUploading ? (
                                <>
                                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 flex flex-col justify-center">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center space-x-3">
                                                <div className="w-10 h-10 rounded bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                                                    <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                                                </div>
                                                <div>
                                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                                        {existingCVProfile?.filename || "No CV uploaded yet"}
                                                    </p>
                                                    {existingCVProfile?.uploaded_at && (
                                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                                            Last updated: {parseFormat(existingCVProfile?.uploaded_at, "D MMMM YYYY")}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 underline"
                                                onClick={() => {
                                                    // Open the CV in a new tab
                                                    window.open(cvURL, "_blank");
                                                }}
                                            >
                                                View
                                            </Button>
                                        </div>

                                        {/* Upload Option within the same field - only show if CV hasn't been uploaded */}
                                        {!hasUploadedCV && (
                                            <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mt-3">
                                                <div onClick={() => setIsOpenUploadCVWarning(true)} className="cursor-pointer">
                                                    <div className="flex items-center justify-center space-x-2 py-2 hover:bg-gray-50 dark:hover:bg-gray-800 rounded transition-colors">
                                                        <svg
                                                            className="h-4 w-4 text-gray-400 dark:text-gray-500"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={2}
                                                                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                                                            />
                                                        </svg>
                                                        <span className="text-sm text-gray-600 dark:text-gray-400">
                                                            Upload a different CV
                                                        </span>
                                                    </div>
                                                </div>
                                                <input
                                                    ref={fileInputRef}
                                                    type="file"
                                                    className="hidden"
                                                    onChange={handleUpload}
                                                    accept=".pdf,.docx"
                                                />
                                            </div>
                                        )}
                                    </div>

                                    {/* Continue Button */}
                                    <Button
                                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3"
                                        disabled={props.isLoadingApply}
                                        onClick={() => props.onContinue(isUploadNewCV)}
                                    >
                                        Continue
                                        <svg
                                            className="ml-2 h-4 w-4"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M5 12h14m-7-7l7 7-7 7"
                                            />
                                        </svg>
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <div className="w-full h-24 flex flex-col items-center justify-center">
                                        <div className="flex space-x-2 mb-4">
                                            {/* Three bouncing blue dots */}
                                            <div
                                                className="w-3 h-3 bg-blue-500 rounded-full animate-bounce"
                                                style={{ animationDelay: "0ms" }}
                                            ></div>
                                            <div
                                                className="w-3 h-3 bg-blue-500 rounded-full animate-bounce"
                                                style={{ animationDelay: "150ms" }}
                                            ></div>
                                            <div
                                                className="w-3 h-3 bg-blue-500 rounded-full animate-bounce"
                                                style={{ animationDelay: "300ms" }}
                                            ></div>
                                        </div>
                                        <p className="text-gray-600 dark:text-gray-400 text-sm">
                                            Uploading your CV...
                                        </p>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Info Notice */}
                        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 mb-4">
                            <div className="flex items-start">
                                <svg
                                    className="h-5 w-5 text-amber-600 dark:text-amber-400 mr-2 flex-shrink-0 mt-0.5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                    />
                                </svg>
                                <p className="text-xs text-amber-800 dark:text-amber-300">
                                    Uploading a new CV will reset your candidate profile and replace all existing information. This cannot be undone.
                                </p>
                            </div>
                        </div>

                        {/* Terms Text */}
                        <p className="text-xs text-center text-gray-500 dark:text-gray-400">
                            By proceeding, you agree to the{" "}
                            <a
                                href="https://ourteam.ai/terms-of-service"
                                target="_blank"
                                className="text-blue-600 dark:text-blue-400 hover:underline"
                            >
                                Terms of Service
                            </a>{" "}
                            and{" "}
                            <a
                                href="https://ourteam.ai/privacy-policy"
                                target="_blank"
                                className="text-blue-600 dark:text-blue-400 hover:underline"
                            >
                                Privacy Policy
                            </a>{" "}
                            at ourteam.
                        </p>
                    </div>
                </div>
            </div>

            <UploadCVWarningDialog
                isOpen={isOpenUploadCVWarning}
                onClose={handleUploadCancel}
                onConfirm={handleUploadConfirm}
            />
        </>
    )
}